import React, { useCallback, useEffect, useRef, useState } from "react";
import { sortBy } from "lodash";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Close } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";
import { PenPortraitInsightVariable } from "@/types";
import { blackOverlay, toIconLabel, toIndex, toPercentage, whiteOverlay } from "@/utils";

interface TopMindsetCardProps {
  title: string;
  variables: PenPortraitInsightVariable[];
  score: string;
  onRemoveSection?: () => void;
  editable: boolean;
}

const TopMindsetCard: React.FC<TopMindsetCardProps> = ({
  title,
  variables,
  score,
  onRemoveSection,
  editable
}) => {
  const theme = useTheme();

  const [scores, setScores] = useState<PenPortraitInsightVariable[]>([]);
  const formatFunc = score === "rate" ? toPercentage : toIndex;

  function useElementWidth() {
    const [width, setWidth] = useState<number>(275);
    const observerRef = useRef<ResizeObserver | null>(null);

    const ref = useCallback((node: HTMLElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();

      if (node) {
        const observer = new ResizeObserver(([entry]) => {
          setWidth(entry.contentRect.width);
        });

        observer.observe(node);
        observerRef.current = observer;
      }
    }, []);

    // Cleanup
    useEffect(() => {
      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    }, []);

    return [ref, width] as const;
  }

  // Get the size of the card
  const [cardRef, cardWidth] = useElementWidth();

  // Calculate the size of the images
  const imageSize = Math.max(Math.floor((cardWidth - 32) / 6) - 8, 32);

  useEffect(() => {
    let filtered = variables
      .filter((x) =>
        score === "rate"
          ? Number(x[score as keyof PenPortraitInsightVariable]) > 0
          : Number(x[score as keyof PenPortraitInsightVariable]) > 0,
      )
    setScores(sortBy(filtered, score).reverse());
  }, [score, variables]);

  const overlay = useCallback((variable: PenPortraitInsightVariable) => {
    if (variable.tag_type === 'Interests') {
      if (theme.palette.mode === 'dark') {
        return whiteOverlay(0.15)
      } else {
        return blackOverlay(0.15)
      }
    } else {
      return undefined
    }
  }, [theme.palette.mode])

  if (scores.length === 0) {
    return null;
  } else {
    return (
      <Card
        key={title}
        sx={{
          border: 1,
          borderRadius: 1,
          borderColor: theme.palette.divider,
          width: "100%",
          containerType: "inline-size",
          position: "relative",
          ":hover": {
            borderColor: editable ? theme.palette.primary.main : undefined,
          },
        }}
      >
        {editable && (
          <HelpBox title="Remove section">
            <IconButton
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                zIndex: 1,
                p: 0.5,
                borderRadius: "6px",
                color: theme.palette.divider,
              }}
              onClick={onRemoveSection}
            >
              <Close fontSize="small" />
            </IconButton>
          </HelpBox>
        )}
        <CardHeader
          title={title}
          slotProps={{ title: { variant: "body2" } }}
          sx={{ height: "56px", alignItems: "flex-start", pb: 0 }}
        />
        <CardContent sx={{ pb: 2, "&:last-child": { pb: 2 } }}>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              width: '100%',
              gap: { xs: 1, sm: 1.5 },
              '@container (max-width: 420px)': {
                '& .item-5': { display: 'none' },
              },
              '@container (max-width: 360px)': {
                '& .item-4': { display: 'none' },
              },
            }}
          >
            {scores.slice(0, 6).map((variable, index) => (
              <Box
                key={variable.tag_id}
                className={`item-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <HelpBox
                  title={`${variable.tag_name} (${formatFunc(
                    Number(
                      variable[score as keyof PenPortraitInsightVariable],
                    ),
                  )})`}
                >
                  <Box
                    component="img"
                    // src={variable.image}
                    src={`https://sc-opendata.s3.eu-west-1.amazonaws.com/mindset_images/${toIconLabel(variable.tag_name)}.png`}
                    alt={variable.tag_name}
                    sx={{
                      width: '100%',
                      maxWidth: '64px',
                      height: '100%',
                      mx: 'auto',
                      objectFit: 'cover',
                      aspectRatio: '1/1',
                      transition: 'transform 0.15s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'scale(1.04)',
                      },
                    }}
                    style={{
                      borderRadius: 5,
                      padding: variable.tag_type === "Interests" ? 4 : undefined,
                      backgroundColor: theme.palette.action.hover,
                      backgroundImage: overlay(variable),
                    }}
                  />
                </HelpBox>
                <Typography
                  variant="caption"
                  sx={{
                    height: "30px",
                    mt: 1,
                    fontSize: "9px",
                    textAlign: "center",
                    display: "-webkit-box",
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                  }}
                >
                  {variable.tag_name}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }
};

export default TopMindsetCard;
